/**
 * useEventStream — Unified SSE hook with multi-channel support
 *
 * Manages a single SSE connection to /api/events/stream with multiple channels.
 * Channels: notifications, metrics, system
 *
 * Usage:
 *   const { subscribe, connected, error } = useEventStream();
 *   
 *   useEffect(() => {
 *     return subscribe('notifications', (event) => {
 *       console.log('Notification:', event);
 *     });
 *   }, [subscribe]);
 *
 * Handles:
 *   - Auto-reconnect with exponential backoff
 *   - Vercel 60s timeout reconnection
 *   - Fallback to polling if SSE unavailable
 *   - React StrictMode double-invoke protection
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from "context/AuthContext";

// Channel types
export const CHANNELS = {
  NOTIFICATIONS: "notifications",
  METRICS: "metrics",
  SYSTEM: "system",
};

// Reconnect settings
const MAX_RECONNECT_DELAY = 30000;
const INITIAL_RECONNECT_DELAY = 1000;
const MAX_CONSECUTIVE_FAILURES = 5;

// Generate unique connection ID per hook instance (per tab)
function generateConnectionId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function useEventStream() {
  const { getIdToken, currentUser } = useAuth();
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [activeChannels, setActiveChannels] = useState([]);

  const eventSourceRef = useRef(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const reconnectTimerRef = useRef(null);
  const failureCountRef = useRef(0);
  const mountedRef = useRef(true);
  const connectingRef = useRef(false);
  // Unique ID for this tab's connection — persists across reconnects
  const connectionIdRef = useRef(generateConnectionId());
  // Counter to track connection attempts (for stale callback detection)
  const connectAttemptRef = useRef(0);
  // Use ref to track active channels without triggering re-renders
  const activeChannelsRef = useRef([]);

  // Subscribers per channel: Map<channel, Set<callback>>
  const subscribersRef = useRef(new Map());
  // Pending channels to add when connected
  const pendingChannelsRef = useRef(new Set());
  // Ref for connect timeout
  const connectTimeoutRef = useRef(null);

  /**
   * Get all currently requested channels from subscribers + pending
   */
  const getRequestedChannels = useCallback(() => {
    const channels = new Set();
    for (const channel of subscribersRef.current.keys()) {
      if (subscribersRef.current.get(channel).size > 0) {
        channels.add(channel);
      }
    }
    for (const channel of pendingChannelsRef.current) {
      channels.add(channel);
    }
    return Array.from(channels);
  }, []);

  /**
   * Connect to the SSE stream (internal, use ref to avoid dependency cycles)
   */
  const connectInternal = useCallback(async () => {
    if (!currentUser || connectingRef.current) return;

    // No channels requested — don't connect
    const channels = getRequestedChannels();
    if (channels.length === 0) return;

    // Already connected with same channels? Skip
    if (
      eventSourceRef.current &&
      eventSourceRef.current.readyState === EventSource.OPEN &&
      JSON.stringify(activeChannelsRef.current.sort()) === JSON.stringify(channels.sort())
    ) {
      return;
    }

    connectingRef.current = true;
    connectAttemptRef.current += 1;
    const thisAttempt = connectAttemptRef.current;

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      const token = await getIdToken();
      if (!token || !mountedRef.current || connectAttemptRef.current !== thisAttempt) {
        connectingRef.current = false;
        return;
      }

      // Build URL with channels and connectionId.
      // In development, connect directly to the backend to bypass CRA proxy
      // which buffers SSE responses and prevents streaming.
      const channelParam = encodeURIComponent(channels.join(","));
      const connId = encodeURIComponent(connectionIdRef.current);
      const base = process.env.NODE_ENV === "development" ? "http://localhost:5001" : "";
      const url = `${base}/api/events/stream?channels=${channelParam}&connectionId=${connId}&token=${encodeURIComponent(token)}`;

      const es = new EventSource(url);
      eventSourceRef.current = es;
      connectingRef.current = false;

      // Track whether we've marked this attempt as connected (avoid repeat setState)
      let markedOpen = false;

      // Mark connection as alive — called from onopen OR first event receipt.
      // CRA dev proxy buffers SSE response headers, so onopen may never fire.
      // Receiving ANY event (message, ping, connected) proves the link is up.
      const markOpen = () => {
        if (markedOpen) return;
        if (!mountedRef.current || connectAttemptRef.current !== thisAttempt) return;
        markedOpen = true;
        activeChannelsRef.current = channels;
        setActiveChannels(channels);
        setConnected(true);
        setError(null);
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
        failureCountRef.current = 0;
        pendingChannelsRef.current.clear();
      };

      // Native onopen — may or may not fire depending on proxy buffering
      es.onopen = markOpen;

      // Server's connected event — also proves link is alive + updates channels
      es.addEventListener("connected", (e) => {
        markOpen();
        if (!mountedRef.current || connectAttemptRef.current !== thisAttempt) return;
        try {
          const data = JSON.parse(e.data);
          if (data.channels) {
            activeChannelsRef.current = data.channels;
            setActiveChannels(data.channels);
          }
        } catch (_) {}
      });

      es.addEventListener("message", (e) => {
        markOpen();
        if (!mountedRef.current || connectAttemptRef.current !== thisAttempt) return;

        try {
          const event = JSON.parse(e.data);
          const { channel, type, data } = event;

          // Dispatch to channel subscribers
          const subs = subscribersRef.current.get(channel);
          if (subs) {
            for (const callback of subs) {
              try {
                callback({ type, data, timestamp: event.timestamp });
              } catch (err) {
                // subscriber error — silently ignore
              }
            }
          }
        } catch (err) {
          // parse error — silently ignore
        }
      });

      es.addEventListener("ping", () => {
        markOpen(); // Ping receipt also proves connection is alive
      });

      es.addEventListener("reconnect", (e) => {
        if (!mountedRef.current || connectAttemptRef.current !== thisAttempt) return;

        let retryMs = 1000;
        try {
          const data = JSON.parse(e.data);
          retryMs = Math.max(data.retryMs || 1000, 1000);
        } catch (_) {}

        es.close();
        eventSourceRef.current = null;
        setConnected(false);

        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) connectInternal();
        }, retryMs);
      });

      es.onerror = () => {
        if (!mountedRef.current || connectAttemptRef.current !== thisAttempt) return;
        es.close();
        eventSourceRef.current = null;
        setConnected(false);
        failureCountRef.current += 1;

        if (failureCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
          setError("SSE connection failed");
          return;
        }

        const delay = Math.min(
          reconnectDelayRef.current * Math.pow(2, failureCountRef.current - 1),
          MAX_RECONNECT_DELAY
        );
        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) connectInternal();
        }, delay);
      };
    } catch (err) {
      connectingRef.current = false;
      setError(err.message);
    }
  }, [currentUser, getIdToken, getRequestedChannels]);

  /**
   * Disconnect from the SSE stream
   */
  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setConnected(false);
    setActiveChannels([]);
    activeChannelsRef.current = [];
  }, []);

  /**
   * Subscribe to a channel with a callback.
   * Returns unsubscribe function.
   * STABLE: does not change between renders
   */
  const subscribe = useCallback(
    (channel, callback) => {
      if (!subscribersRef.current.has(channel)) {
        subscribersRef.current.set(channel, new Set());
      }
      subscribersRef.current.get(channel).add(callback);

      // If not connected to this channel yet, trigger reconnect
      if (!activeChannelsRef.current.includes(channel)) {
        pendingChannelsRef.current.add(channel);
        // Reconnect to include new channel - debounce multiple subscribe calls
        if (connectTimeoutRef.current) {
          clearTimeout(connectTimeoutRef.current);
        }
        connectTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) connectInternal();
        }, 100);
      }

      // Return unsubscribe function
      return () => {
        const subs = subscribersRef.current.get(channel);
        if (subs) {
          subs.delete(callback);
        }
      };
    },
    [connectInternal]
  );

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [disconnect]);

  return useMemo(
    () => ({
      subscribe,
      connected,
      error,
      activeChannels,
      disconnect,
      CHANNELS,
    }),
    [subscribe, connected, error, activeChannels, disconnect]
  );
}
